import classNames from 'classnames';
import React from 'react';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import * as styles from './TopicPrivacyIcon.module.scss';
import getTopicPrivacyInfo from './getTopicPrivacyInfo';

const GET_TOPIC_FRAGMENT = gql(`
  fragment getTopicPrivacyIcon_topic on TopicFields {
    id
    privacy
    ... on Topic {
      memberEditable
    }
  }
`);

const TOPIC_FRAGMENT = gql(`
  fragment TopicPrivacyIcon_topic on TopicFields {
    id
    ...getTopicPrivacyIcon_topic
  }
`);

const SIZES = {
  small: 10,
  regular: 12,
  medium: 14,
  large: 18,
} as const;

interface GetTopicPrivacyIconOptions {
  iconSize?: keyof typeof SIZES;
  showDefaultIcon?: boolean;
  tooltip?: boolean;
  topic: DocumentType<typeof GET_TOPIC_FRAGMENT>;
}

export const getTopicPrivacyIcon = ({
  topic,
  iconSize = 'small',
  showDefaultIcon,
  tooltip,
}: GetTopicPrivacyIconOptions) => {
  const privacyInfo = getTopicPrivacyInfo(
    topic.privacy,
    'memberEditable' in topic ? topic.memberEditable : undefined,
  );

  if (!privacyInfo || (privacyInfo.isDefault && !showDefaultIcon)) return null;

  const inner = (
    <SVGIcon inline size={SIZES[iconSize]}>
      {privacyInfo.icon}
    </SVGIcon>
  );

  return (
    <div className={classNames(styles.iconContainer, styles[iconSize])}>
      {tooltip ? (
        <Tooltip content={privacyInfo.helpText}>{inner}</Tooltip>
      ) : (
        inner
      )}
    </div>
  );
};

export interface TopicPrivacyIconProps extends GetTopicPrivacyIconOptions {
  topic: DocumentType<typeof TOPIC_FRAGMENT>;
}

const TopicPrivacyIcon = ({
  iconSize = 'small',
  showDefaultIcon,
  topic,
  tooltip,
}: TopicPrivacyIconProps) => {
  return getTopicPrivacyIcon({ topic, iconSize, showDefaultIcon, tooltip });
};

export default TopicPrivacyIcon;
